import type { ReactNode } from "react";
import styled, { keyframes } from "styled-components";

import { color } from "../../theme/color";
import { IconButton, SmallHeading } from "./controls";
import { Icon } from "./Icon";

/** A panel that slides over the page from the left, in place of a back button. */
export function SlideOver(props: {
    label: string;
    caption?: string;
    title: string;
    children: ReactNode;
    footer?: ReactNode;
    onClose(): void;
}) {
    return (
        <>
            <Scrim aria-label={`Close ${props.label}`} onClick={props.onClose} />
            <Panel aria-label={props.label} onKeyDown={(event) => event.key === "Escape" && props.onClose()}>
                <Header>
                    <div style={{ flex: 1 }}>
                        {props.caption && <SmallHeading as="div">{props.caption}</SmallHeading>}
                        <Title>{props.title}</Title>
                    </div>
                    <IconButton aria-label={`Close ${props.label}`} onClick={props.onClose}>
                        <Icon name="close" size={20} />
                    </IconButton>
                </Header>
                <Body>{props.children}</Body>
                {props.footer && <Footer>{props.footer}</Footer>}
            </Panel>
        </>
    );
}

const slideIn = keyframes`
    from { transform: translateX(-100%); }
`;

const fadeIn = keyframes`
    from { opacity: 0; }
`;

const Scrim = styled.button`
    position: fixed;
    inset: 0;
    z-index: 10;
    border: 0;
    padding: 0;
    background: ${color.scrim};
    cursor: pointer;
    animation: ${fadeIn} 0.2s ease-out;
`;

const Panel = styled.nav`
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    z-index: 11;
    width: min(380px, 85vw);
    background: ${color.background};
    box-shadow: 8px 0 32px ${color.shadow};
    display: flex;
    flex-direction: column;
    animation: ${slideIn} 0.22s ease-out;
`;

const Header = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 18px 10px 10px 20px;
`;

const Title = styled.h2`
    margin: 2px 0 0;
    font-size: 21px;
    font-weight: 700;
`;

const Body = styled.div`
    flex: 1;
    overflow: auto;
    padding: 0 10px;
`;

const Footer = styled.div`
    display: flex;
    flex-direction: column;
    padding: 8px 20px 20px;
    border-top: 1px solid ${color.border};
`;
